import React, { useState, useEffect, useRef } from 'react';
import { Search, MapPin, Loader2, X } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';

const SearchInput = ({ onSearch, isLoading, onClearResults }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&addressdetails=1`;
        const response = await fetch(url, {
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'GeoCenter App (educational project)'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setSuggestions(data);
          setShowSuggestions(data.length > 0);
        }
      } catch (err) {
        console.error('Suggestion fetch error:', err);
      } finally {
        setSearchLoading(false);
      }
    }, 500);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  const handleSelect = (suggestion) => {
    setQuery(suggestion.display_name.split(',')[0]);
    setSuggestions([]);
    setShowSuggestions(false);
    if (onClearResults) onClearResults();
    onSearch(suggestion.display_name);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      if (onClearResults) onClearResults();
      onSearch(query);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Введите город..."
              className="pl-10 pr-10 h-12 text-base bg-white border-slate-300 focus:border-sky-500 focus:ring-sky-500"
              disabled={isLoading}
            />
            {query && !isLoading && !searchLoading && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            {searchLoading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
            )}
          </div>
          <Button
            type="submit"
            disabled={isLoading || !query.trim()}
            className="h-12 px-4 sm:px-6 bg-sky-600 hover:bg-sky-700 text-white font-medium transition-all"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                <Search className="h-5 w-5 sm:hidden" />
                <span className="hidden sm:inline">Найти</span>
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-2 bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden">
          <ul className="py-2 max-h-80 overflow-y-auto">
            {suggestions.map((suggestion, idx) => (
              <li
                key={suggestion.place_id || idx}
                onClick={() => handleSelect(suggestion)}
                className="px-4 py-3 hover:bg-slate-50 cursor-pointer flex items-start gap-3 transition-colors"
              >
                <MapPin className="h-5 w-5 text-sky-500 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-slate-700 block">{suggestion.display_name}</span>
                  <span className="text-xs text-slate-500">{suggestion.type}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchInput;
